import type { EffectiveLimitSnapshot } from '@/features/publishing/services/publishing';

const LIMIT_WARNING_THRESHOLD = 0.8;
const TRIAL_ENDING_SOON_DAYS = 3;

export type BannerTone = 'warning' | 'critical';

export type BannerConfig = {
    tone: BannerTone;
    title: string;
    description: string;
    actionLabel: string;
};

export function resolvePublishingLimitBanner(
    snapshots: EffectiveLimitSnapshot[],
    targetNameById: Map<string, string>,
    t: (key: string, options?: Record<string, unknown>) => string,
): BannerConfig | null {
    const sortedSnapshots = [...snapshots].sort((left, right) => {
        const leftRatio = getUsageRatio(left);
        const rightRatio = getUsageRatio(right);

        return rightRatio - leftRatio;
    });

    const exhausted = sortedSnapshots.find((snapshot) => snapshot.remainingAllowance < 1);
    if (exhausted) {
        const accountName = targetNameById.get(exhausted.subject.id) ?? t('organizations.limitBanner.connectedAccountFallback', {
            defaultValue: 'this connected account',
        });

        return {
            tone: 'critical',
            title: t('organizations.limitBanner.publishingReachedTitle', {
                defaultValue: 'Publishing quota reached',
            }),
            description: t('organizations.limitBanner.publishingReachedDescription', {
                defaultValue:
                    '{{accountName}} has used all {{limit}} posts for the current billing period. Upgrade to keep publishing.',
                accountName,
                limit: exhausted.configuredLimit,
            }),
            actionLabel: t('organizations.limitBanner.upgradeAction', {
                defaultValue: 'Upgrade plan',
            }),
        };
    }

    const nearLimit = sortedSnapshots.find((snapshot) => getUsageRatio(snapshot) >= LIMIT_WARNING_THRESHOLD);
    if (!nearLimit) {
        return null;
    }

    const accountName = targetNameById.get(nearLimit.subject.id) ?? t('organizations.limitBanner.connectedAccountFallback', {
        defaultValue: 'this connected account',
    });

    return {
        tone: 'warning',
        title: t('organizations.limitBanner.publishingWarningTitle', {
            defaultValue: 'Approaching publishing quota',
        }),
        description: t('organizations.limitBanner.publishingWarningDescription', {
            defaultValue:
                '{{accountName}} has used {{currentUsage}} of {{limit}} posts for the current billing period. Upgrade before publishing gets blocked.',
            accountName,
            currentUsage: nearLimit.currentUsage,
            limit: nearLimit.configuredLimit,
        }),
        actionLabel: t('organizations.limitBanner.upgradeAction', {
            defaultValue: 'Upgrade plan',
        }),
    };
}

export function isTrialEndingSoonNow(trialEndsAt: string | null | undefined): boolean {
    if (!trialEndsAt) {
        return false;
    }

    const trialEndTime = Date.parse(trialEndsAt);
    if (Number.isNaN(trialEndTime)) {
        return false;
    }

    const timeRemaining = trialEndTime - Date.now();
    return timeRemaining > 0 && timeRemaining <= TRIAL_ENDING_SOON_DAYS * 24 * 60 * 60 * 1000;
}

function getUsageRatio(snapshot: EffectiveLimitSnapshot): number {
    if (snapshot.configuredLimit <= 0) {
        return 0;
    }

    return snapshot.currentUsage / snapshot.configuredLimit;
}
